import { refreshLoadNotesAfterReconnect } from './utils/load-notes-sync'

import { refreshDriverStatusRows } from '../schedulerSlice'
import { ApplicationBaseMessage, DebugBaseMessage, TeamPeopleChangedMessage } from '@numeo/types-main-web-app'
import {
    AssistantLoadBaseMessage,
    BoxTruckLoadsWsMessage,
    CallActionTypes,
    CallBaseMessage,
    LoadFinderBaseMesage,
    NotificationMessage,
    UpdaterBaseMessage,
} from '@numeo/types'
import { WsMessage, WsMessageCategory } from '@numeo/types-core'
import { Middleware } from '@reduxjs/toolkit'
import { fetchActiveAiHubCalls } from 'features/agentic-hub/services/agenticHubApi'
import { removeTab } from 'features/agentic-hub/store/agenticHubSlice'
import { replayMissedEvents } from 'features/agentic-hub/store/replay-handler'
import { cleanupTabEventCache } from 'features/agentic-hub/store/socket-handlers'
import { refreshGeneralAgentAfterReconnect } from 'features/general-agent'
import { Category } from 'hooks/useNotification'
import { sendWebSocketMessage } from 'services/websocketApi'
import { getAuthToken } from 'setup/SetupAxios'
import { refreshNotesAfterReconnect, resetDriverNotes } from '@/services/driverNoteCache'
import { wssHostUrl } from 'utils/network'
import { logout, setApplication } from '../authSlice'
import { setAiHubActiveCalls } from '../callCenterSlice'
import { upsertNotification } from '../notificationSlice'
import { connected, connecting, disconnected, error, forceDisconnect, markConnectedOnce, replayCompleted, replayStarted } from '../webSocketSlice'
import { createSocketWorker } from './createSocketWorker'
import {
    handleAgenticHubMessages,
    handleApplicationMessages,
    handleAssistantLoadMessages,
    handleBoxTruckLoadsMessages,
    handleCallCenterMessage,
    handleDebugMessage,
    handleLoadFinderMessages,
    handleNotificationMessage,
} from './handlers'
import { handleAgentActivityMessages } from './handlers/agent-activity'
import { handleLiveLoadSearchMessage } from './handlers/live-load-search'
import { handleTeamMessages } from './handlers/team'
import { handleUpdaterMessages } from './handlers/updater'
import { TabMessageType, WorkerMessageType, type WorkerToTabMessage } from './workerProtocol'

let sharedWorker: SharedWorker | null = null
let port: MessagePort | null = null
let registeredWithWorker = false
let sharedWorkerUnavailable = false

const REALTIME_UNAVAILABLE_MESSAGE = 'Real-time updates are unavailable in this browser'

function setupWorker(): MessagePort | null {
    if (sharedWorker && port) return port
    if (sharedWorkerUnavailable) return null

    try {
        const worker = createSocketWorker()
        if (!worker) {
            sharedWorkerUnavailable = true
            console.warn(`[WebSocket] ${REALTIME_UNAVAILABLE_MESSAGE}; continuing without a live socket.`)
            return null
        }
        const workerPort = worker.port
        workerPort.start()
        sharedWorker = worker
        port = workerPort
        return workerPort
    } catch (workerError) {
        // Keep an unsupported/restricted WebView usable even if accessing the worker port itself fails.
        sharedWorker = null
        port = null
        sharedWorkerUnavailable = true
        console.warn(`[WebSocket] ${REALTIME_UNAVAILABLE_MESSAGE}; continuing without a live socket.`, workerError)
        return null
    }
}

export const webSocketMiddleware: Middleware = (store) => {
    const { dispatch, getState } = store

    function handleWorkerMessage(evt: MessageEvent<WorkerToTabMessage>) {
        const msg = evt.data

        switch (msg.type) {
            case WorkerMessageType.SOCKET_CONNECTED: {
                // Read the flag BEFORE dispatching connected() so we capture whether this is
                // the first connect of the session vs a reconnect.
                const hasConnectedOnce = getState().webSocket.hasConnectedOnce
                dispatch(connected())
                const getActiveCallsMessage = {
                    categoryType: WsMessageCategory.CallCenter,
                    data: { action: CallActionTypes.CC_GetActiveCalls },
                }
                sendWebSocketMessage(getActiveCallsMessage).catch((err) => {
                    console.error('Error sending get active calls message:', err)
                })

                fetchActiveAiHubCalls()
                    .then((calls) => dispatch(setAiHubActiveCalls(calls)))
                    .catch((err) => console.error('Error fetching active AI Hub calls:', err))

                // If this is a reconnect (not the first connect), ask socket-service for any
                // Agentic Hub events that were published while we were offline and dispatch them
                // through the normal handler pipeline so the UI catches up seamlessly.
                if (hasConnectedOnce) {
                    dispatch(refreshDriverStatusRows())
                    dispatch(replayStarted())
                    replayMissedEvents(dispatch, getState())
                        .catch((err) => console.error('[Replay] Unhandled error during replay', err))
                        .finally(() => dispatch(replayCompleted()))
                    refreshNotesAfterReconnect()
                    refreshLoadNotesAfterReconnect(store.dispatch, store.getState)
                    refreshGeneralAgentAfterReconnect()
                } else {
                    dispatch(markConnectedOnce())
                }
                break
            }
            case WorkerMessageType.ALREADY_CONNECTED: {
                dispatch(connected())
                // The SharedWorker's socket is already open — this tab hasn't missed anything
                // so no replay is needed right now. But we must mark this tab as "already
                // connected once" so that if the socket later drops and reconnects, the
                // SOCKET_CONNECTED handler treats it as a reconnect and fires replay.
                dispatch(markConnectedOnce())
                fetchActiveAiHubCalls()
                    .then((calls) => dispatch(setAiHubActiveCalls(calls)))
                    .catch((err) => console.error('Error fetching active AI Hub calls:', err))
                break
            }
            case WorkerMessageType.STATUS: {
                if (msg.status === 'connecting') {
                    // Don't re-dispatch connecting() if we triggered it - only for worker-initiated reconnects
                    const state = getState()
                    if (state.webSocket.status !== 'connecting') {
                        dispatch(connecting())
                    }
                } else if (msg.status === 'disconnected') {
                    dispatch(disconnected(msg.error || null))
                } else if (msg.status === 'error') {
                    dispatch(error(msg.error || 'Unknown error'))
                }
                break
            }
            case WorkerMessageType.MESSAGE: {
                try {
                    const parsedData = JSON.parse(msg.payload) as WsMessage
                    if (parsedData) {
                        const numeoMessage = parsedData
                        switch (numeoMessage.categoryType) {
                            case WsMessageCategory.CallCenter:
                                handleCallCenterMessage(numeoMessage as CallBaseMessage, dispatch)
                                break
                            case WsMessageCategory.LoadFinder:
                                handleLoadFinderMessages(numeoMessage as LoadFinderBaseMesage, dispatch, getState)
                                break
                            case WsMessageCategory.Updater:
                                handleUpdaterMessages(numeoMessage as UpdaterBaseMessage, dispatch, getState)
                                break
                            case WsMessageCategory.Debug:
                                handleDebugMessage(numeoMessage as DebugBaseMessage)
                                break
                            case WsMessageCategory.Notification:
                                handleNotificationMessage(numeoMessage as NotificationMessage, dispatch)
                                break
                            case WsMessageCategory.AssistantLoad:
                                handleAssistantLoadMessages(numeoMessage as AssistantLoadBaseMessage, dispatch)
                                break
                            case WsMessageCategory.Application:
                                handleApplicationMessages(numeoMessage as ApplicationBaseMessage, dispatch, getState)
                                break
                            case WsMessageCategory.AgentActivity:
                                handleAgentActivityMessages(numeoMessage, dispatch)
                                break
                            case WsMessageCategory.AgentStep:
                                break
                            case WsMessageCategory.AgenticHub:
                                handleAgenticHubMessages(numeoMessage, dispatch, getState)
                                break
                            case WsMessageCategory.BoxTruckLoads:
                                handleBoxTruckLoadsMessages(numeoMessage as BoxTruckLoadsWsMessage)
                                break
                            case WsMessageCategory.LiveLoadSearch:
                                handleLiveLoadSearchMessage(numeoMessage)
                                break
                            case WsMessageCategory.Team:
                                handleTeamMessages(numeoMessage as TeamPeopleChangedMessage, dispatch, getState)
                                break
                            default:
                                console.error('WS event not handled', parsedData)
                                return
                        }
                    }
                } catch (err) {
                    console.error('Error handling WebSocket message:', err)
                }
                break
            }
            case WorkerMessageType.FORCE_DISCONNECT: {
                dispatch(forceDisconnect(msg.reason))
                dispatch(
                    upsertNotification({
                        id: 'duplicate_connection',
                        category: Category.Error,
                        message: 'You were disconnected due to another active session. Refresh the page to reconnect.',
                        timestamp: Date.now(),
                        type: 'warning',
                        topic: 'duplicate_connection',
                    })
                )
                break
            }
        }
    }

    return (next) => (action) => {
        if (connecting.match(action)) {
            // Prevent re-registering when we dispatch connecting() from worker status
            if (registeredWithWorker) {
                return next(action)
            }

            const token = getAuthToken()
            if (!token) {
                console.error('WebSocket connection failed: No auth token available')
                dispatch(error('No auth token available'))
                return next(action)
            }

            const workerPort = setupWorker()
            if (!workerPort) {
                // Pass the original connecting action through first, then settle the store as disconnected;
                // dispatching in the opposite order lets connecting() overwrite this fallback state.
                const result = next(action)
                dispatch(disconnected(REALTIME_UNAVAILABLE_MESSAGE))
                return result
            }
            workerPort.onmessage = handleWorkerMessage
            workerPort.postMessage({ type: TabMessageType.REGISTER, token, wssUrl: wssHostUrl() })
            registeredWithWorker = true

            window.addEventListener(
                'beforeunload',
                () => {
                    if (port && registeredWithWorker) {
                        port.postMessage({ type: TabMessageType.UNREGISTER })
                    }
                },
                { once: true }
            )
        }

        if (disconnected.match(action) && action.payload === 'MasterLayout unmounted') {
            if (port && registeredWithWorker) {
                port.postMessage({ type: TabMessageType.UNREGISTER })
                registeredWithWorker = false
            }
        }

        // Release per-tab dedup cache when the tab is closed so the Map doesn't grow unbounded.
        if (removeTab.match(action)) {
            cleanupTabEventCache(action.payload)
        }

        if (logout.match(action) || (setApplication.match(action) && getState().auth?.application?.id !== action.payload?.id)) resetDriverNotes()
        return next(action)
    }
}
